import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Rich%20editorial%20fashion%20still%20life%20with%20layered%20textures%20silk%20scarves%20leather%20goods%20and%20metallic%20accessories%20scattered%20across%20dark%20charcoal%20slate%20surface%20dramatic%20side%20lighting%20from%20floor%20lamp%20creating%20deep%20shadows%20and%20warm%20amber%20glow%20contemporary%20luxury%20aesthetic%20with%20moody%20atmosphere%20and%20rich%20color%20depth&width=1920&height=1080&seq=hero-bg-slate-july2026&orientation=landscape"
          alt="Superbuy Spreadsheet curated product collection"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>

      <div ref={dropdownRef} className="absolute top-6 left-6 md:top-8 md:left-8 z-30">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center gap-2 px-5 py-2.5 bg-foreground-900/60 backdrop-blur-sm text-background-50 rounded-full text-sm font-semibold tracking-wider cursor-pointer hover:bg-foreground-900/80 transition-all duration-200 whitespace-nowrap"
        >
          CATEGORIES
          {dropdownOpen ? (
            <i className="ri-arrow-up-s-line text-base w-5 h-5 flex items-center justify-center"></i>
          ) : (
            <i className="ri-arrow-down-s-line text-base w-5 h-5 flex items-center justify-center"></i>
          )}
        </button>

        {dropdownOpen && (
          <div className="absolute top-full mt-2 left-0 bg-foreground-900/90 backdrop-blur-md rounded-2xl py-3 px-2 min-w-[260px] shadow-lg animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-background-50/90 hover:text-background-50 hover:bg-background-50/10 rounded-xl transition-colors duration-150 whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 flex flex-col lg:flex-row items-center lg:items-end justify-between max-w-7xl mx-auto pt-16">
        <div className="flex flex-col items-start lg:pb-16 max-w-2xl">
          <span className="text-background-50/80 text-sm font-semibold tracking-[0.2em] mb-4 font-label">
            SUPERBUY SPREADSHEET HUB
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading leading-tight mb-6">
            <span className="text-background-50 font-light block">Superbuy Spreadsheet</span>
            <span className="text-background-50 font-bold block">The Ultimate Resource For Smarter Shopping</span>
          </h1>
          <p className="text-background-50/80 text-base md:text-lg max-w-xl mb-8 leading-relaxed">
            Discover organized Superbuy spreadsheets, product guides, QC tips, comparison tools, and expert resources designed to help you find products faster and shop with confidence.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-6 py-3 border-2 border-background-50 text-background-50 rounded-full font-semibold text-sm hover:bg-background-50 hover:text-foreground-950 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Superbuy Sheet
              <i className="ri-arrow-right-up-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-950 rounded-full font-semibold text-sm hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>
        </div>

        <div className="lg:pb-16 max-w-md">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex -space-x-2">
              <div className="w-10 h-10 rounded-full border-2 border-background-50/40 overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Extreme%20close%20up%20of%20woven%20textile%20detail%20showing%20intricate%20thread%20patterns%20in%20warm%20russet%20and%20sand%20tones%20macro%20photography%20with%20very%20shallow%20depth%20of%20field%20soft%20diffused%20window%20light%20revealing%20organic%20fabric%20texture%20and%20natural%20fibers&width=80&height=80&seq=avatar-textile-july2026&orientation=squarish"
                  alt="Product thumbnail"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-10 h-10 rounded-full border-2 border-background-50/40 overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Macro%20detail%20shot%20of%20polished%20metal%20hardware%20on%20leather%20accessory%20gleaming%20brass%20buckle%20against%20rich%20cognac%20leather%20background%20soft%20reflected%20light%20catching%20metallic%20surface%20editorial%20accessory%20photography%20with%20warm%20golden%20tones&width=80&height=80&seq=avatar-brass-july2026&orientation=squarish"
                  alt="Product thumbnail"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-10 h-10 rounded-full border-2 border-background-50/40 overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20close%20up%20of%20layered%20translucent%20fabrics%20in%20sage%20green%20and%20dusty%20rose%20tones%20backlit%20by%20morning%20sunlight%20creating%20ethereal%20glowing%20effect%20textile%20art%20photography%20with%20dreamy%20soft%20focus%20and%20delicate%20shadow%20play&width=80&height=80&seq=avatar-fabric-july2026&orientation=squarish"
                  alt="Product thumbnail"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <span className="text-background-50/80 text-xs">Trusted by smart shoppers worldwide</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10">
        <div className="animate-bounce">
          <i className="ri-arrow-down-line text-background-50/60 text-2xl w-6 h-6 flex items-center justify-center"></i>
        </div>
      </div>
    </section>
  );
}